import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, List, Modal, Typography } from 'antd';
import { ShieldCheck } from 'lucide-react';
import PageModal from '@/components/PageModal';
import Axios from 'axios';

const { Text } = Typography;

/**
 * One-call e-signature for approval / release / hold acts (qa-head F1 F2 F5, prod-sup F8, plant-head F16).
 *
 * The page collects {username, password, comment} here and sends them WITH the decision as
 * `esign: {username, password, comment}` — the backend verifies the password, writes the
 * ESignatureRecord and links it to the act in the same transaction. No separate
 * create_esignature call, so a signature can never be detached from what it authorises.
 *
 * props: open, title, action ('approve' | 'reject' | custom label), meaning (text shown under the
 * title), requireReason (true for reject / withdraw / hold), reasonLabel, busy, onCancel,
 * onSubmit({ username, password, comment }) — the caller performs the request.
 * usernameReadOnly (REX-01 R-F1): the signer is the logged-in person; the username is shown, not typed.
 * refusal (EXV-039): the caller's last refusal of this act; when it changes to a refusal the password is cleared,
 * so a refused signature is never re-sent by pressing Enter again. While `busy`, OK and Enter do nothing.
 */
export default function ApprovalSignModal({ open, title, action = 'approve', meaning, requireReason = false, reasonLabel, busy = false, onCancel, onSubmit, children, usernameReadOnly = false, refusal = null }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const isReject = String(action).toLowerCase() === 'reject';

  useEffect(() => {
    if (open) {
      const stored = (() => { try { return JSON.parse(localStorage.getItem('user_details') || '{}'); } catch (e) { return {}; } })();
      setUsername(stored?.username || stored?.user_name || '');
      setPassword(''); setComment(''); setError('');
    }
  }, [open]);

  useEffect(() => {
    if (refusal) setPassword('');
  }, [refusal]);

  const submit = () => {
    if (busy) return;
    if (!username.trim() || !password) { setError('Username and password are required to sign.'); return; }
    if (requireReason && comment.trim().length < 5) { setError(`${reasonLabel || 'A reason'} is required (at least 5 characters).`); return; }
    setError('');
    onSubmit?.({ username: username.trim(), password, comment: comment.trim() });
  };

  const heading = title || (isReject ? 'Reject — e-signature' : 'Approve — e-signature');
  const okLabel = isReject ? 'Reject & sign' : `${action === 'approve' ? 'Approve' : action} & sign`;
  return (
    <PageModal
      open={open}
      icon={<ShieldCheck size={22} />}
      title={heading}
      description="Your signature is recorded with this action (21 CFR Part 11)."
      onCancel={onCancel}
      width={520}
      destroyOnHidden
      maskClosable={false}
      zIndex={1600}
      footer={(
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" danger={isReject} loading={busy} onClick={submit}>{okLabel}</Button>
        </div>
      )}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        {meaning && <Alert type={isReject ? 'warning' : 'info'} showIcon message={meaning} />}
        {children}
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{reasonLabel || (requireReason ? 'Reason' : 'Comment')}{requireReason ? ' *' : ''}</div>
          <Input.TextArea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={requireReason ? 'State the reason — it is recorded with the signature' : 'Optional comment recorded with the signature'} />
        </div>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Username *</div>
          <Input autoComplete="off" value={username} readOnly={usernameReadOnly} disabled={usernameReadOnly}
            aria-label="Username" onChange={(e) => setUsername(e.target.value)} />
        </div>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Password * <span style={{ fontWeight: 400, opacity: 0.7 }}>(re-authentication, 21 CFR 11 §11.200)</span></div>
          <Input.Password autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} onPressEnter={submit} />
        </div>
        {error && <Alert type="error" showIcon message={error} />}
      </div>
    </PageModal>
  );
}

/** Failing gate checks from a 409 body ({gate:{items}} or {reasons}) — [] when the error is not a gate refusal. */
export function gateReasons(errOrBody) {
  const body = errOrBody?.response?.data || errOrBody?.data || errOrBody || {};
  const items = body?.gate?.items || body?.data?.items || [];
  const failed = items.filter((i) => i && i.ok === false).map((i) => `${i.key}${i.detail ? ` — ${i.detail}` : ''}`);
  if (failed.length) return failed;
  if (Array.isArray(body?.reasons)) return body.reasons;
  return [];
}

/** The message a page should toast for a refused act (401 signature · 403 role / SoD · 409 gate · 400 reason). */
export function actErrorMessage(err, fallback = 'Action refused') {
  const body = err?.response?.data || err?.data || {};
  return body?.message || body?.detail || body?.Authorization || err?.message || fallback;
}

/** Show the gate's check list when the backend answered 409 with the reasons; returns true when it did. */
export function showGateReasons(err, title = 'Release gate refused') {
  const reasons = gateReasons(err);
  if (!reasons.length) return false;
  Modal.warning({
    title,
    width: 640,
    content: (
      <List size="small" dataSource={reasons} renderItem={(r) => <List.Item><Text>{r}</Text></List.Item>} />
    ),
  });
  return true;
}

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token') || ''}` } });

/** POST an act (decision + esign in one body) to /bp/<path>; resolves with the body, rejects with the axios error (response.data carries code / gate). */
export function postAct(path, body) {
  let plantId = null;
  try { plantId = JSON.parse(localStorage.getItem('active-plant') || 'null')?.id || null; } catch (e) { plantId = null; }
  const url = path.startsWith('/bp/') ? path : `/bp/${path.replace(/^\//, '')}`;
  return Axios.post(url, plantId ? { ...body, plant_id: plantId } : body, authHeaders()).then((res) => res.data);
}
